import { Box, Typography } from '@mui/material';
import {
  IStyles,
  pxTovW,
  pxToRem,
  Loader,
  PrimaryButton,
  deserify,
  useCommonServiceClientContext,
  getLocalStorage,
  ActionsPopup,
  useAndroidAppRefreshGate,
} from '@geneo2-web/shared-ui';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../reduxStore/reduxHooks';
import { useEffect, useState } from 'react';
import {
  ISteps,
  StepperComponent,
} from '../../QuestionPaperGenerator/QuestionPaperCreate/components/Stepper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setToastInfo,
  setToastInfoV2,
} from '../../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import {
  AssessmentCreationEnum,
  SubSectionType,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { getDifficultyLevelFromString } from '../../Common/ReviewAssessment/functions';
import { getMiddleApproximateTime } from '../../utils/assessment.utils';
import {
  FA_DRAFT,
  FA_REVIEW,
  HOME,
  QPG_ASSESSMENT_PUBLISH,
  QPG_ASSESSMENT_REVIEW_PAPER,
} from '../../../../routeHandling/RoutesNomenclature';
import {
  setFACreatedSectionTest,
  setFASelectedChapters,
  setFASettingsConfig,
  setQPGChapterIdsInfoCollection,
  setQPGCreatedSectionTest,
} from '../../reducer/assessmentFlow.slice';

import {
  ChapterIdsInfo,
  ChapterInfo,
  MarkingSchemeEnum,
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  MultiSubjectSubSection,
  QuestionTypeMarks,
  TaskCreationStatusEnum,
  TaskEnum,
  TaskStudentAttemptStatusEnum,
  TopicIdsInfo,
} from '@protos/learning_management/lms.db_pb';
import { Question } from '@protos/content_management/content.db_pb';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { FACreationChapterSelection } from './components/FACreationChapterSelection';
import {
  getUniqueQuestionIdsFromFAContent,
  getUpdatedSectionChapterInfoFromQuestionModuleMap,
} from '../FAv2.utils';
import {
  buildTaskInfoModels,
  mergeChapterTopicsInfoUnion,
} from '../../utils/QPGv2.utils';
import { FACreationSettings } from './components/FACreationSettings';
import { ValidationPopup } from '../../QuestionPaperGenerator/QuestionPaperCreate/components/ValidationPopup';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(15), md: pxTovW(50), lg: pxTovW(240) },
    paddingTop: { xs: pxToRem(20), md: pxTovW(50) },
    paddingBottom: { xs: pxToRem(85), md: pxTovW(50) },
  },
  buttonContainer: {
    position: { xs: 'fixed', md: 'unset' },
    top: { xs: '92%', md: 'unset' },
    width: { xs: '90%', md: pxTovW(397) },
    height: { xs: pxToRem(50), md: pxTovW(80), lg: pxTovW(60) },
    marginLeft: 'auto',
    zIndex: { xs: 1, md: 0 },
  },
};

export const FACreation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teacherId = getLocalStorage('userId');
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get('subject_id');
  const editFlow = searchParams.get('edit_flow');
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const {
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const user_info = useDeserifiedSelector(
    (state) => state.auth.user_info
  );
  const { manage_mode_of_conduction } = useAppSelector(
    (state) => state.manageHomework
  );
  const {
    qpg_subsection_data,

    fa_content,
    fa_selected_section_number,
    fa_selected_chapters,
    fa_settings_config,
    fa_format_details,
  } = deserify(useAppSelector((state) => state.assessment));

  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(
    editFlow === 'chapter' ? 0 : editFlow === 'bloom' ? 1 : 0
  );
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const [isTemplateValid, setIsTemplateValid] = useState<boolean>(false);
  const [confirmCreateAssessmentPopup, setConfirmCreateAssessmentPopup] =
    useState<boolean>(false);
  const [exitConfirmPopup, setExitConfirmPopup] = useState<
    'back' | 'logo' | undefined
  >(undefined);
  const [noQuestionsAvailablePopup, setNoQuestionsAvailablePopup] =
    useState<boolean>(false);

  // --------------------------------------------------------------

  // --------------------------------------------------------------

  const applyQuestionMarksToSubSectionMetaData = (props: {
    subSection: MultiSubjectSubSection;
    questions: Question[];
  }) => {
    const { subSection, questions } = props;

    if (!subSection?.subSectionMetaData?.length || !questions?.length) {
      return subSection;
    }

    const questionMarksMap = new Map<string, number>();

    questions.forEach((question) => {
      const questionId = question.questionId;
      const marks =
        question.question?.model.value?.commonQuestionContent?.marks?.[0];

      if (questionId && typeof marks === 'number') {
        questionMarksMap.set(questionId, marks);
      }
    });

    subSection.subSectionMetaData = subSection.subSectionMetaData.map(
      (meta) => {
        const metaQuestionId = meta?.questionId;
        if (!metaQuestionId) {
          return meta;
        }

        const maxMarks = questionMarksMap.get(metaQuestionId);
        if (typeof maxMarks === 'number') {
          meta.maxMarks = maxMarks;
        }

        return meta;
      }
    );

    return subSection;
  };

  // --------------------------------------------------------------

  const createTestHandler = async () => {
    const currentSectionData = fa_content?.multiSubjectSections?.find(
      (s) => s.sectionNo === fa_selected_section_number
    );

    try {
      setLoading('loading');
      const getModuleQuestionsWithFiltersResponse =
        await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
          {
            teacherId: BigInt(teacherId),
            subjectId: currentSectionData?.subjectId,
            sectionId: fa_format_details?.selectedSection?.id,
            assessmentCreationType:
              AssessmentCreationEnum.ASSESSMENT_CREATION_FORMATIVE,
            avgDifficultyLevel: getDifficultyLevelFromString(
              fa_settings_config?.overall_difficulty || 'Medium'
            ),
            bloomLevels: fa_settings_config?.bloom_levels || [],
            moduleIds: fa_selected_chapters?.chapters.flatMap((chapter) => [
              chapter.chapterId,
              ...chapter.topics.map((topic) => topic.topicId),
            ]),
            approximateTime: getMiddleApproximateTime({
              difficulty: fa_settings_config?.overall_difficulty,
              questionCount: fa_settings_config?.no_of_questions,
            }),
            modeOfConduction: fa_content?.assessment?.modeOfConduction,
            noOfQuestions: fa_settings_config?.no_of_questions,
            requiredQuestionTypes: fa_settings_config?.question_types,

            qIdsToBeIgnored: getUniqueQuestionIdsFromFAContent({
              faContent: fa_content,
              sectionNumbersToIgnore:
                fa_selected_section_number !== undefined
                  ? [fa_selected_section_number]
                  : [],
            }),
          }
        );

      if (
        getModuleQuestionsWithFiltersResponse.message.includes(
          'No questions found'
        )
      ) {
        setNoQuestionsAvailablePopup(true);
        setLoading('error');
        return;
      }

      if (getModuleQuestionsWithFiltersResponse.status === 200) {
        const multiSubjectSectionMetaInfo =
          getModuleQuestionsWithFiltersResponse?.data
            ?.multiSubjectSectionMetaInfo || [];

        if (multiSubjectSectionMetaInfo[0]) {
          multiSubjectSectionMetaInfo[0].chapterInfo =
            getUpdatedSectionChapterInfoFromQuestionModuleMap({
              section: multiSubjectSectionMetaInfo[0],
              questionModuleMap:
                getModuleQuestionsWithFiltersResponse?.data?.questionModuleMap,
            });
        }

        // Only one section data is coming and is incomplete
        // complete current section data using fa_content and response
        // and preserve other sections data from fa_content

        const metaInfo =
          fa_content?.multiSubjectSections?.map(
            (section: MultiSubjectSection, index) => {
              const isSelectedSection =
                section.sectionNo === fa_selected_section_number;

              if (!isSelectedSection) return section;

              const responseSubSectionMeta = multiSubjectSectionMetaInfo[0];
              responseSubSectionMeta.multiSubjectSubSections =
                responseSubSectionMeta.multiSubjectSubSections.map(
                  (subSection, index) => {
                    subSection.questionTypeAndMarks =
                      qpg_subsection_data?.[index]?.subSectionQuestions.map(
                        (question) => {
                          return new QuestionTypeMarks({
                            questionType: question.questionType,
                            marks: question.marks,
                          });
                        }
                      ) || [];

                    if (
                      fa_settings_config?.marking_scheme ===
                      MarkingSchemeEnum.MARKING_SCHEME_JEE
                    ) {
                      return applyCustomMaxMarksToSubSectionMetaData({
                        subSection: subSection,
                        maxMarks: 4,
                      });
                    } else if (
                      fa_settings_config?.marking_scheme ===
                      MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
                    ) {
                      return applyCustomMaxMarksToSubSectionMetaData({
                        subSection: subSection,
                        maxMarks:
                          fa_content.questionsSequenceInfo?.marksPerQuestion ||
                          0,
                      });
                    }

                    return applyQuestionMarksToSubSectionMetaData({
                      subSection,
                      questions:
                        getModuleQuestionsWithFiltersResponse.data?.questions ??
                        [],
                    });
                  }
                );

              return new MultiSubjectSection({
                ...responseSubSectionMeta,
                multiSubjectSectionStatus:
                  MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_DRAFT,
                preferredMarks: section.preferredMarks,
                sectionNo: section.sectionNo,
                sectionTitle: section.sectionTitle,
                sectionName: section.sectionName,
                assessmentSectionAttemptStatus:
                  TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_PENDING,
                markingSchemeType: fa_settings_config?.marking_scheme,
              });
            }
          ) || [];

        const faChapters =
          fa_content?.assessment?.taskMetaInfo?.chapterInfo
            ?.chapterTopicsInfo || [];
        const moduleChapters =
          getModuleQuestionsWithFiltersResponse.data?.taskMetaInfo?.chapterInfo
            ?.chapterTopicsInfo || [];

        // Keep loader visible while create call is in-flight as well.
        setLoading('loading');
        const assessmentCreationSubmitResponse =
          await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
            {
              teacherId: BigInt(teacherId),
              assessmentId: fa_content?.assessment?.assessmentId,
              classId: fa_content?.assessment?.classId,
              creationStatus: TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
              multiSubjectSectionMetaInfo: metaInfo,
              sectionId: fa_content?.assessment?.sectionId,
              subjectIds:
                fa_content?.multiSubjectSections.map(
                  (section) => section.subjectId
                ) ?? [],
              taskName: fa_content?.assessment?.assessmentTitle,
              taskType: fa_content?.assessment?.taskType,
              instructions: fa_content?.assessment?.assessmentInstructions,
              questionModuleMap: (() => {
                const combined = [
                  ...(fa_content?.questionsSequenceInfo?.questionModuleMap ??
                    []),
                  ...(getModuleQuestionsWithFiltersResponse?.data
                    ?.questionModuleMap ?? []),
                ];
                const seen = new Set<string>();
                return combined.filter((m) => {
                  const key = `${m.questionId ?? ''}|${m.chapterId ?? ''}|${
                    m.topicId ?? ''
                  }`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
              })(),
              modeOfConduction: manage_mode_of_conduction,
              selectedTasksInfo: buildTaskInfoModels(metaInfo),
              marksPerQuestion:
                fa_content?.questionsSequenceInfo?.marksPerQuestion,
              // chapterIdsInfo: mergeChapterTopicsInfoUnion(
              //   faChapters,
              //   moduleChapters
              // ),
            }
          );

        if (assessmentCreationSubmitResponse.status === 200) {
          setLoading('completed');
          navigate(FA_REVIEW);
          dispatch(
            setFACreatedSectionTest(getModuleQuestionsWithFiltersResponse.data)
          );
        } else {
          setLoading('error');
          dispatch(
            setToastInfo({
              label: 'Error while creating assessment',
              variant: 'error',
              open: true,
            })
          );
        }
      } else {
        setLoading('error');
        dispatch(
          setToastInfo({
            label: 'Error while creating assessment',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      setLoading('error');
      console.log('error:', error);
      let message = 'Error while creating assessment';
      if (error instanceof ConnectError) {
        message = error.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      dispatch(
        setToastInfo({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // --------------------------------------------------------------

  const createTestButtonDisabled = () => {
    return true;
  };

  // --------------------------------------------------------------

  const getPreferredMarks = (): number => {
    const data = fa_content?.multiSubjectSections?.find(
      (e) => e.sectionNo === fa_selected_section_number
    );

    return data?.preferredMarks ?? 0;
  };

  // --------------------------------------------------------------

  const getTotalMarks = (): number => {
    const data = qpg_subsection_data ?? [];
    return data.reduce((sectionAcc, subsection) => {
      const subsectionMarks = (subsection.subSectionQuestions ?? []).reduce(
        (acc, curr) => acc + (curr.noOfQuestion || 0) * (curr.marks || 0),
        0
      );
      return sectionAcc + subsectionMarks;
    }, 0);
  };

  // --------------------------------------------------------------

  const steps: ISteps[] = [
    {
      index: 0,
      stepTitle: 'Chapter Selection',
      stepClickHandler: async () => {
        setSelectedStepIndex(0);
        console.log('Step 1 Clicked');
        // await interactionEvent({
        //   context: 'Question_paper_generator',
        //   name: 'CHAPTER_SELECTION',
        //   pathSegments: pathSegments,
        //   isOffline: isOffline,
        // });
      },
      stepButtonText: 'NEXT',
      disableStep:
        fa_selected_chapters === undefined ||
        fa_selected_chapters?.chapters?.length === 0,
      stepIconColor: '#007CDC',
      disabledIconColor: '#7EC6FD',
    },

    {
      index: 1,
      stepTitle: 'Settings',
      stepClickHandler: async () => {
        setSelectedStepIndex(1);
        console.log('Step 2 Clicked');
        // await interactionEvent({
        //   context: 'Question_paper_generator',
        //   name: 'SETTINGS',
        //   pathSegments: pathSegments,
        //   isOffline: isOffline,
        // });
      },
      stepButtonText: 'CREATE TEST',
      disableStep:
        fa_selected_chapters === undefined ||
        fa_selected_chapters?.chapters?.length === 0 ||
        fa_settings_config?.no_of_questions === 0 ||
        fa_settings_config?.question_types?.length === 0,
      stepIconColor: '#0AA34F',
      disabledIconColor: '#A9EBC9',
    },
  ];

  // --------------------------------------------------------------

  const handleNextClick = async () => {
    // if (selectedStepIndex === steps.length - 1) {
    //   setCreateAssessmentPopupOpen(true);
    // } else {
    //   setSelectedStepIndex(selectedStepIndex + 1);
    // }

    if (selectedStepIndex === steps.length - 1) {
      if (getPreferredMarks() === getTotalMarks()) {
        createTestHandler();
      } else {
        setConfirmCreateAssessmentPopup(true);
      }
    } else {
      setSelectedStepIndex(selectedStepIndex + 1);
    }

    // await interactionEvent({
    //   context: 'Question_paper_generator',
    //   name: (steps[selectedStepIndex]?.stepButtonText || 'NEXT')
    //     .toUpperCase()
    //     .replace(/\s+/g, '_'),
    //   pathSegments: pathSegments,
    //   isOffline: isOffline,
    // });
  };

  // --------------------------------------------------------------

  const backClickHandler = () => {
    if (editFlow === 'chapter' || editFlow === 'bloom') {
      navigate(
        FA_DRAFT + `?assessment_id=${fa_content?.assessment?.assessmentId}`
      );
    } else setExitConfirmPopup('back');
  };
  const logoClickHandler = () => {
    setExitConfirmPopup('logo');
  };

  useEffect(() => {
    setSelectedFunction(() => backClickHandler);
    setSelectedLogoClickHandler(() => logoClickHandler);
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [exitConfirmPopup]);

  // --------------------------------------------------------------

  // Chapter Selection and Settings steps are long, content-heavy pages
  // where native Android's scroll-position-based pull-to-refresh guard
  // isn't reliable enough on its own. Disable native app refresh for the
  // whole lifetime of this flow, same as ReviewAssessment/SearchQuestions.
  useAndroidAppRefreshGate();

  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          // alignItems: 'center',
          marginBottom: { xs: pxToRem(23), md: pxTovW(41) },
          display: 'flex',
        }}
      >
        <Typography variant="h1">Formative Assessment</Typography>
        <PrimaryButton
          sx={styles.buttonContainer}
          disabled={steps[selectedStepIndex]?.disableStep}
          onClick={handleNextClick}
        >
          <Typography variant="h3" color="common.white" fontWeight="bold">
            {steps[selectedStepIndex]?.stepButtonText || 'Next'}
          </Typography>
        </PrimaryButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: { xs: pxToRem(40), md: pxTovW(60) },
          marginBottom: { xs: pxToRem(30), md: pxTovW(50) },
        }}
      >
        <StepperComponent currentStep={selectedStepIndex} steps={steps} />
      </Box>

      {selectedStepIndex === 0 ? (
        <FACreationChapterSelection />
      ) : (
        <FACreationSettings />
      )}

      <ActionsPopup
        open={confirmCreateAssessmentPopup}
        handleClose={() => {
          setConfirmCreateAssessmentPopup(false);
        }}
        fontSmall
        iconName="error_yellow"
        popupText={`Total marks (${getTotalMarks()}) don't match Preferred marks (${getPreferredMarks()}).`}
        splitText="Do you want to create assessment?"
        yesClickHandler={async () => {
          createTestHandler();
          setConfirmCreateAssessmentPopup(false);
        }}
        noClickHandler={async () => {
          setConfirmCreateAssessmentPopup(false);
        }}
      />

      <ActionsPopup
        open={!!exitConfirmPopup}
        handleClose={() => {
          setExitConfirmPopup(undefined);
        }}
        fontSmall
        iconName="error_yellow"
        popupText="If you go back, you may lose your changes."
        splitText="Are you sure you want go back?"
        yesClickHandler={async () => {
          if (exitConfirmPopup === 'back') {
            navigate(
              FA_DRAFT +
                `?assessment_id=${fa_content?.assessment?.assessmentId}`
            );
          } else {
            navigate(HOME);
          }
          setExitConfirmPopup(undefined);
          dispatch(setFASelectedChapters(undefined));
          dispatch(setFASettingsConfig(undefined));
        }}
        noClickHandler={async () => {
          setExitConfirmPopup(undefined);
        }}
      />

      <ValidationPopup
        open={noQuestionsAvailablePopup}
        handleClose={() => {
          setNoQuestionsAvailablePopup(false);
          navigate(
            FA_DRAFT + `?assessment_id=${fa_content?.assessment?.assessmentId}`
          );
        }}
        okHandler={() => {
          setNoQuestionsAvailablePopup(false);
          navigate(
            FA_DRAFT + `?assessment_id=${fa_content?.assessment?.assessmentId}`
          );
        }}
        iconname="error"
        text1={'No questions are available for this subject.'}
      />
    </Box>
  );
};

export const applyCustomMaxMarksToSubSectionMetaData = (props: {
  subSection: MultiSubjectSubSection;
  maxMarks: number;
}) => {
  const { subSection, maxMarks } = props;

  if (!subSection?.subSectionMetaData?.length) return subSection;

  subSection.subSectionMetaData = subSection.subSectionMetaData.map((meta) => {
    meta.maxMarks = maxMarks;
    if (meta?.alternateQuestions?.length) {
      meta.alternateQuestions = meta.alternateQuestions.map((alt) => {
        alt.maxMarks = maxMarks;
        return alt;
      });
    }
    return meta;
  });

  return subSection;
};
